import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CONSENT_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'granted', label: 'Granted' },
  { value: 'denied', label: 'Denied' },
];

const SOURCE_OPTIONS = [
  { value: 'Website', label: 'Website' },
  { value: 'Referral', label: 'Referral' },
  { value: 'Event', label: 'Event' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Cold Call', label: 'Cold Call' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Other', label: 'Other' },
];

export default function ContactsFormModal({ isOpen, contact, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'Website',
    whatsappConsent: 'unknown',
    tags: '',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact?.name || '',
        email: contact?.email || '',
        phone: contact?.phone || '',
        source: contact?.source || 'Website',
        whatsappConsent: contact?.whatsappConsent || 'unknown',
        tags: Array.isArray(contact?.tags) ? contact.tags.join(', ') : '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        source: 'Website',
        whatsappConsent: 'unknown',
        tags: '',
      });
    }
    setErrors({});
  }, [contact, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    const hasEmail = formData.email.trim();
    const hasPhone = formData.phone.trim();
    if (!hasEmail && !hasPhone) newErrors.email = 'Email or phone is required';
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email required';
    if (hasPhone && !/^[+\d][\d\s-]{6,}$/.test(formData.phone)) newErrors.phone = 'Valid phone required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(formData);
    } catch {
      // Parent surfaces the error; the modal stays open for retry.
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          {contact ? 'Edit Contact' : 'Add New Contact'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Full Name *</label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Contact name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@example.com"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Phone</label>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+91 98765 43210"
              className={errors.phone ? 'border-red-500' : ''}
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Source</label>
            <Select
              name="source"
              value={formData.source}
              onChange={(value) => handleSelectChange("source", value)}
              options={SOURCE_OPTIONS}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">WhatsApp Consent</label>
            <Select
              name="whatsappConsent"
              value={formData.whatsappConsent}
              onChange={(value) => handleSelectChange("whatsappConsent", value)}
              options={CONSENT_OPTIONS}
            />
            <p className="text-xs text-gray-500 mt-1">
              Follow-up drafts are only allowed for contacts with consent granted.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Tags</label>
            <Input
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="vip, hot-lead (comma separated)"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={onClose}
              disabled={saving}
              className="flex-1 bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            >
              {saving ? 'Saving…' : contact ? 'Update Contact' : 'Add Contact'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}